import { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatAge(seconds) {
  if (seconds == null) return 'never';
  if (seconds < 60) return 'just now';
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    return `${m} min${m === 1 ? '' : 's'} ago`;
  }
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    return `${h} hr${h === 1 ? '' : 's'} ago`;
  }
  const d = Math.floor(seconds / 86400);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export default function OddsRefreshChip() {
  const [age, setAge] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/epl-odds/status`, { withCredentials: true });
      setAge(res.data?.age_seconds ?? null);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // Re-poll every 60 seconds so the "X mins ago" counter stays honest
    // without a hard refresh.
    const id = setInterval(loadStatus, 60_000);
    return () => clearInterval(id);
  }, [loadStatus]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await axios.post(`${API_URL}/api/epl-odds/refresh`, {}, { withCredentials: true });
      if (res.data?.throttled) {
        const remaining = res.data.cooldown_seconds_remaining || 0;
        const mins = Math.ceil(remaining / 60);
        toast.info(`Odds were refreshed recently — try again in ${mins} min${mins === 1 ? '' : 's'}.`);
      } else {
        const s = res.data?.summary || {};
        toast.success(
          `Odds refreshed · ${s.fixtures_updated ?? 0} fixtures updated`
        );
      }
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refresh failed');
    }
    setRefreshing(false);
  };

  const label = age == null
    ? 'Odds not yet refreshed'
    : `Odds last refreshed · ${formatAge(age)}`;

  return (
    <div
      className="inline-flex items-center gap-2 text-xs text-gray-500 bg-gray-100 rounded-full px-3 py-1.5 hover:bg-gray-200 transition-colors"
      data-testid="epl-odds-refresh-chip"
    >
      <span className="font-medium" data-testid="epl-odds-refresh-label">{label}</span>
      <span className="text-gray-300">·</span>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={refreshing}
        className="inline-flex items-center gap-1 font-semibold text-[#37003C] hover:text-[#5a0063] disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="epl-odds-refresh-btn"
      >
        <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Refreshing…' : 'Refresh now'}
      </button>
    </div>
  );
}
